// components/groupAdmin/feed/create/CourseCreateCard.jsx
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { adminFeedApi } from '../feedApi';

/**
 * Creates a "Course" feed item + a draft course record (backend).
 * After creation, open the CourseBuilder to add modules + video lessons.
 */
export default function CourseCreateCard({ groupId, onCourseCreated }) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const reset = () => {
    setTitle('');
    setDescription('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error(t('groupAdminTabs.feed.toastTitleRequired', { defaultValue: 'Title is required' }));
      return;
    }

    try {
      setCreating(true);
      const res = await adminFeedApi.createCourse(groupId, {
        title: title.trim(),
        description: description.trim(),
      });

      // Support different backend responses
      const courseId = res?.data?.course_id || res?.data?.courseId || res?.data?.id;
      const feedItemId = res?.data?.feed_item_id || res?.data?.feedItemId || null;

      toast.success(t('groupAdminTabs.feed.courseCreated', { defaultValue: 'Course created (draft)' }));
      reset();
      onCourseCreated?.({ courseId, feedItemId });
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || t('groupAdminTabs.feed.courseCreateError', { defaultValue: 'Failed to create course' }));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('groupAdminTabs.feed.createCourseTitle', { defaultValue: 'Create a course' })}</CardTitle>
        <CardDescription>
          {t('groupAdminTabs.feed.createCourseDescription', { defaultValue: 'Create a Udemy-style course and then add modules + video lessons.' })}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <Label>{t('groupAdminTabs.feed.titleField', { defaultValue: 'Title' })}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Beginner Guitar: Week 1" required />
          </div>

          <div>
            <Label>{t('groupAdminTabs.feed.descriptionField', { defaultValue: 'Description' })}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Short course summary…" />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={reset} disabled={creating}>
              {t('groupAdminTabs.feed.reset', { defaultValue: 'Reset' })}
            </Button>
            <Button type="submit" className="flex-1" disabled={creating}>
              {creating ? t('groupAdminTabs.feed.creating', { defaultValue: 'Creating…' }) : t('groupAdminTabs.feed.createCourseBtn', { defaultValue: 'Create course' })}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
